import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Eye, Download, Search, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export default function AdminResults() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | premium | free
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState(""); // yyyy-mm-dd
  const [to, setTo] = useState("");

  useEffect(() => {
    api
      .get("/admin/results")
      .then((res) => setItems(res.data.items))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    items.forEach((r) => { if (r.diagnostic_category) set.add(r.diagnostic_category); });
    return Array.from(set).sort();
  }, [items]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromTs = from ? new Date(from + "T00:00:00").getTime() : null;
    const toTs   = to   ? new Date(to   + "T23:59:59").getTime() : null;
    return items.filter((r) => {
      if (filter === "premium" && !r.is_premium) return false;
      if (filter === "free" && r.is_premium) return false;
      if (category !== "all" && r.diagnostic_category !== category) return false;
      if (fromTs && new Date(r.created_at).getTime() < fromTs) return false;
      if (toTs   && new Date(r.created_at).getTime() > toTs)   return false;
      if (q) {
        const hay = [
          r.diagnostic_id,
          r.diagnostic_title?.ja, r.diagnostic_title?.en,
          r.diagnostic_category,
          r.user_id, r.user_email,
          r.type_label?.ja, r.type_label?.en,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, filter, category, search, from, to]);

  const counts = {
    all: items.length,
    premium: items.filter((r) => r.is_premium).length,
    free: items.filter((r) => !r.is_premium).length,
  };

  const exportCSV = () => {
    if (visible.length === 0) {
      toast.error("No rows to export");
      return;
    }
    const headers = [
      "id", "created_at", "diagnostic_id", "diagnostic_title_ja",
      "category", "user_id", "user_email", "overall", "iq_score",
      "type_label", "is_premium", "lang",
    ];
    const rows = visible.map((r) => [
      r.id,
      r.created_at,
      r.diagnostic_id,
      r.diagnostic_title?.ja || r.diagnostic_title?.en || "",
      r.diagnostic_category,
      r.user_id || "",
      r.user_email || "",
      r.overall,
      r.diagnostic_id === "iq" ? Math.round(70 + r.overall * 1.1) : "",
      r.type_label?.ja || r.type_label?.en || "",
      r.is_premium ? "1" : "0",
      r.lang || "",
    ]);
    const csv = "\ufeff" + [headers, ...rows].map((row) => row.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `iqlog-results-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${visible.length} rows`);
  };

  const clearFilters = () => {
    setFilter("all"); setCategory("all"); setSearch(""); setFrom(""); setTo("");
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">// RESULTS</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight" style={{ fontWeight: 800 }}>
            All diagnostic results
          </h1>
          <p className="text-sm text-[#94A3B8] mt-2">
            {visible.length} of {items.length} entries · click any row to view the full result
          </p>
        </div>
        <button
          type="button"
          onClick={exportCSV}
          disabled={visible.length === 0}
          data-testid="results-export-csv"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#3B82F6] text-black font-mono text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA] disabled:opacity-40 rounded-md"
        >
          <Download className="h-3.5 w-3.5" strokeWidth={2.5} />
          Export CSV ({visible.length})
        </button>
      </div>

      {/* Filters */}
      <div className="bg-[#0F1525] border border-[#1E2A3F] rounded-md p-4 mb-5">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {[
            { key: "all", label: "All" },
            { key: "premium", label: "Premium" },
            { key: "free", label: "Free" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`results-filter-${f.key}`}
              className={
                "px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border rounded-md transition-colors " +
                (filter === f.key
                  ? "border-[#3B82F6] text-[#3B82F6] bg-[#3B82F6]/10"
                  : "border-[#1E2A3F] text-[#94A3B8] hover:text-white hover:border-[#3B82F6]")
              }
            >
              {f.label} <span className="text-[#64748B] ml-1.5">{counts[f.key]}</span>
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative sm:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#64748B]" strokeWidth={2} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by diagnostic, user, type…"
              data-testid="results-search-input"
              className="w-full bg-[#0A0E1A] border border-[#1E2A3F] pl-9 pr-3 py-2.5 text-sm text-white font-mono"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="results-category-select"
            className="bg-[#0A0E1A] border border-[#1E2A3F] px-3 py-2.5 text-sm text-white font-mono"
          >
            <option value="all">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="results-date-from"
              className="w-1/2 bg-[#0A0E1A] border border-[#1E2A3F] px-2 py-2.5 text-xs text-white font-mono"
            />
            <span className="text-[#64748B] font-mono text-xs">→</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="results-date-to"
              className="w-1/2 bg-[#0A0E1A] border border-[#1E2A3F] px-2 py-2.5 text-xs text-white font-mono"
            />
          </div>
        </div>

        {(filter !== "all" || category !== "all" || search || from || to) && (
          <button
            type="button"
            onClick={clearFilters}
            data-testid="results-clear-filters"
            className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] hover:text-[#3B82F6]"
          >
            <X className="h-3 w-3" strokeWidth={2} />
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#64748B]">// Loading…</div>
      ) : visible.length === 0 ? (
        <div className="bg-[#0F1525] border border-[#1E2A3F] rounded-md p-10 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
            // NO RESULTS
          </div>
        </div>
      ) : (
        <div className="bg-[#0F1525] border border-[#1E2A3F] rounded-md overflow-x-auto">
          <table className="w-full" data-testid="results-table">
            <thead>
              <tr className="border-b border-[#1E2A3F] text-left">
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Created</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Diagnostic</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Category</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">User</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Score</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Type</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Status</th>
                <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`result-row-${r.id}`}
                  className="border-b border-[#1E2A3F]/60 hover:bg-[#0A0E1A]/60 transition-colors group"
                >
                  <td className="px-5 py-3 font-mono text-[11px] text-[#64748B] whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-sm text-white">
                    {r.diagnostic_title?.ja || r.diagnostic_id}
                  </td>
                  <td className="px-5 py-3 text-sm text-[#94A3B8]">{r.diagnostic_category}</td>
                  <td className="px-5 py-3 font-mono text-[11px] text-[#94A3B8]">
                    {r.user_id ? (
                      <span title={r.user_id}>{r.user_id.slice(0, 8) + "…"}</span>
                    ) : (
                      <span className="text-[#64748B]">anon</span>
                    )}
                  </td>
                  <td className="px-5 py-3 font-mono text-sm text-[#3B82F6] tabular-nums">
                    {r.diagnostic_id === "iq"
                      ? `IQ ${Math.round(70 + r.overall * 1.1)}`
                      : r.overall}
                  </td>
                  <td className="px-5 py-3 text-sm text-[#94A3B8]">
                    {r.type_label?.ja || r.type_label?.en || "—"}
                  </td>
                  <td className="px-5 py-3">
                    {r.is_premium ? (
                      <span className="inline-block px-2 py-0.5 rounded bg-[#00FF94]/15 text-[#00FF94] border border-[#00FF94]/40 font-mono text-[10px] uppercase tracking-[0.2em]">
                        ✓ premium
                      </span>
                    ) : (
                      <span className="font-mono text-[10px] text-[#64748B] uppercase tracking-[0.2em]">free</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Link
                      to={`/result/${r.id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`view-result-${r.id}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#1E2A3F] hover:border-[#3B82F6] hover:text-[#3B82F6] rounded font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] transition-colors"
                    >
                      <Eye className="h-3 w-3" strokeWidth={2} />
                      View
                      <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
